import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DEFAULT_LIVE_TLB_USAGE_MAPPING } from '../utils/liveTlbUsageMapping';

const TSR_UNITS = ['Hours', 'Landings', 'Years', 'Calendar Days', 'Cycles', 'Hobbs Meter', 'Hobbs Meter AC'];

function ensureShape(v) {
  const base = {
    byUnit: { ...DEFAULT_LIVE_TLB_USAGE_MAPPING.byUnit },
    engineHoursByPosition: { ...DEFAULT_LIVE_TLB_USAGE_MAPPING.engineHoursByPosition },
    labelRules: []
  };
  if (!v || typeof v !== 'object') return base;
  return {
    byUnit: { ...base.byUnit, ...(v.byUnit || {}) },
    engineHoursByPosition: { ...base.engineHoursByPosition, ...(v.engineHoursByPosition || {}) },
    labelRules: Array.isArray(v.labelRules) ? v.labelRules.map((r) => ({ ...r })) : []
  };
}

/**
 * Editable live TLB usage mapping (TSR unit → totalCarriedForward keys).
 * @param {{ value: object, onChange: (next: object) => void, disabled?: boolean, idPrefix?: string }} props
 */
export default function LiveTlbUsageMappingEditor({ value, onChange, disabled = false, idPrefix = 'tlb-map' }) {
  const m = ensureShape(value);

  const setByUnit = (unit, keyStr) => {
    const trimmed = String(keyStr || '').trim();
    const next = ensureShape(value);
    next.byUnit = { ...next.byUnit, [unit]: trimmed === '' ? null : trimmed };
    onChange(next);
  };

  const setEngine = (pos, keyStr) => {
    const trimmed = String(keyStr || '').trim();
    const next = ensureShape(value);
    next.engineHoursByPosition = { ...next.engineHoursByPosition, [pos]: trimmed };
    onChange(next);
  };

  const addLabelRule = () => {
    const next = ensureShape(value);
    next.labelRules = [...next.labelRules, { unit: 'Cycles', label: '', tlbKey: '' }];
    onChange(next);
  };

  const updateLabelRule = (index, field, v) => {
    const next = ensureShape(value);
    next.labelRules = next.labelRules.map((r, i) => (i === index ? { ...r, [field]: v } : r));
    onChange(next);
  };

  const removeLabelRule = (index) => {
    const next = ensureShape(value);
    next.labelRules = next.labelRules.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          TSR unit → TLB totalCarriedForward field
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave empty to use no field for that unit (live due/remaining shows N/A unless a label rule matches). Hours, Hobbs Meter, Hobbs Meter AC, and Landings defaults match standard TLB totals.
        </Typography>
        <GridLike>
          {TSR_UNITS.map((unit) => (
            <TextField
              key={unit}
              id={`${idPrefix}-unit-${unit}`}
              label={unit}
              size="small"
              fullWidth
              disabled={disabled}
              value={m.byUnit[unit] == null ? '' : String(m.byUnit[unit])}
              onChange={(e) => setByUnit(unit, e.target.value)}
              placeholder={DEFAULT_LIVE_TLB_USAGE_MAPPING.byUnit[unit] || 'customKey'}
            />
          ))}
        </GridLike>
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Engine assembly — Hours (TLB keys)
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            id={`${idPrefix}-eng-lh`}
            label="LH"
            size="small"
            disabled={disabled}
            value={m.engineHoursByPosition.LH || ''}
            onChange={(e) => setEngine('LH', e.target.value)}
          />
          <TextField
            id={`${idPrefix}-eng-rh`}
            label="RH"
            size="small"
            disabled={disabled}
            value={m.engineHoursByPosition.RH || ''}
            onChange={(e) => setEngine('RH', e.target.value)}
          />
          <TextField
            id={`${idPrefix}-eng-def`}
            label="Default (e.g. N/A position)"
            size="small"
            fullWidth
            disabled={disabled}
            value={m.engineHoursByPosition.default || ''}
            onChange={(e) => setEngine('default', e.target.value)}
          />
        </Stack>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1">Label rules (optional)</Typography>
          <Button startIcon={<AddIcon />} onClick={addLabelRule} disabled={disabled} size="small">
            Add rule
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          First matching rule (unit + exact label text, case-insensitive) wins—for example different cycle counters per TSR label.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Unit</TableCell>
                <TableCell>Label (must match TSR)</TableCell>
                <TableCell>TLB field key</TableCell>
                <TableCell width={80} />
              </TableRow>
            </TableHead>
            <TableBody>
              {m.labelRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No label rules
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                m.labelRules.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ minWidth: 140 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Unit</InputLabel>
                        <Select
                          label="Unit"
                          value={row.unit || 'Cycles'}
                          disabled={disabled}
                          onChange={(e) => updateLabelRule(idx, 'unit', e.target.value)}
                        >
                          {TSR_UNITS.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        disabled={disabled}
                        value={row.label || ''}
                        onChange={(e) => updateLabelRule(idx, 'label', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        disabled={disabled}
                        value={row.tlbKey || ''}
                        onChange={(e) => updateLabelRule(idx, 'tlbKey', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        color="error"
                        size="small"
                        disabled={disabled}
                        onClick={() => removeLabelRule(idx)}
                        aria-label="Remove rule"
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}

function GridLike({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 2
      }}
    >
      {children}
    </Box>
  );
}
